import { useEffect, useState, type FormEvent } from 'react';

import {
  MS_PER_SECOND,
  SECONDS_PER_MINUTE,
} from '@/shared/constants/app/time.constant';
import { EMAIL_VERIFY_RESEND_COOLDOWN_SECONDS } from '../../constants/cooldown.constant';
import { InlineError, PrimaryButton, TextButton } from './OnboardingUi';

const CODE_LENGTH = 6;
/** 남은 시간이 이보다 적으면 붉게 표시한다. */
const EXPIRY_WARNING_SECONDS = 60;

interface CodeVerifierProps {
  /** 마지막 발송 시각(epoch ms). 재발송하면 부모가 `key` 로 이 컴포넌트를 새로 그린다. */
  sentAt: number;
  ttlSeconds: number;
  onSubmit: (code: string) => void;
  submitting: boolean;
  submitLabel: string;
  error?: string;
  onResend: () => void;
  resending: boolean;
}

/** 1초마다 현재 시각을 갱신한다 — 남은 시간·재발송 대기 표시용. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), MS_PER_SECOND);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

const SECONDS_DIGITS = 2;
const toMmss = (s: number) =>
  `${Math.floor(s / SECONDS_PER_MINUTE)}:${String(s % SECONDS_PER_MINUTE).padStart(SECONDS_DIGITS, '0')}`;

/**
 * 6자리 인증번호 입력. 여섯 자리가 채워지면 바로 확인을 보낸다.
 * 재발송 대기는 서버 쿨다운과 같은 값 — 먼저 풀리면 429 를 맞는다(휴대폰 발송 제한은 이보다 느슨하다).
 */
export function CodeVerifier({
  sentAt,
  ttlSeconds,
  onSubmit,
  submitting,
  submitLabel,
  error,
  onResend,
  resending,
}: CodeVerifierProps) {
  const [code, setCode] = useState('');
  const now = useNow();
  const elapsed = Math.max(Math.floor((now - sentAt) / MS_PER_SECOND), 0);
  const remaining = Math.max(ttlSeconds - elapsed, 0);
  const resendIn = Math.max(EMAIL_VERIFY_RESEND_COOLDOWN_SECONDS - elapsed, 0);
  const expired = remaining === 0;

  const submit = (value: string) => {
    if (value.length === CODE_LENGTH && !submitting && !expired)
      onSubmit(value);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submit(code);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" noValidate>
      <label className="block">
        <span className="sr-only">인증번호 6자리</span>
        <div className="relative">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={CODE_LENGTH}
            placeholder="000000"
            value={code}
            disabled={expired}
            aria-invalid={!!error}
            onChange={(e) => {
              const next = e.target.value
                .replace(/\D/g, '')
                .slice(0, CODE_LENGTH);
              setCode(next);
              if (next.length === CODE_LENGTH && code.length !== CODE_LENGTH)
                submit(next);
            }}
            className={[
              'block h-12 w-full rounded-lg border bg-white px-16 text-center font-mono text-xl tracking-[0.4em]',
              'placeholder:text-neutral-300 dark:bg-dark-card dark:text-neutral-100 dark:placeholder:text-neutral-600',
              'focus-visible:outline-none focus-visible:ring-2',
              'disabled:cursor-not-allowed disabled:bg-neutral-100 dark:disabled:bg-white/10',
              error
                ? 'border-danger focus-visible:ring-danger'
                : 'border-neutral-300 focus-visible:ring-brand-500 dark:border-white/15',
            ].join(' ')}
          />
          <span
            aria-live="off"
            className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium tabular-nums ${
              expired || remaining <= EXPIRY_WARNING_SECONDS
                ? 'text-danger'
                : 'text-neutral-500 dark:text-neutral-400'
            }`}
          >
            {expired ? '만료' : toMmss(remaining)}
          </span>
        </div>
      </label>

      {error && <InlineError>{error}</InlineError>}
      {expired && (
        <InlineError>인증번호가 만료되었습니다. 다시 받아 주세요.</InlineError>
      )}

      <PrimaryButton
        type="submit"
        disabled={code.length < CODE_LENGTH || submitting || expired}
      >
        {submitting ? '확인 중…' : submitLabel}
      </PrimaryButton>

      <p className="flex items-center justify-center gap-1.5 text-xs text-neutral-600 dark:text-neutral-400">
        인증번호를 받지 못하셨나요?
        <TextButton onClick={onResend} disabled={resendIn > 0 || resending}>
          {resending
            ? '보내는 중…'
            : resendIn > 0
              ? `다시 받기 (${resendIn}초)`
              : '다시 받기'}
        </TextButton>
      </p>
    </form>
  );
}
